import { RouteObject } from 'react-router-dom';

import { Match } from './RouteWatcher';

export const ROUTE_KEYS = {
  // Routes with a __CONTAINER key aren't intended to be rendered as a standlone route, but rather
  // are a container for their children.
  __CONTAINER: '__CONTAINER',
  ROOT_REDIRECT: 'ROOT_REDIRECT',
  // PUBLIC_ERROR differs from the standard ERROR route in that it is also used for potentially unauthenticated users,
  // so we don't attempt to render any kind of contextual UI, just a message.
  PUBLIC_ERROR: 'PUBLIC_ERROR',
  NOT_FOUND: 'NOT_FOUND',
  // the FUNCTIONAL_REDIRECT path is intended to be a single interface for backend redirects to hit, such that we
  // can refactor the frontend routes and update the redirect path in one place, rather than update the frontend
  // and backend paths separately and require a phased deploy process with backcompat
  FUNCTIONAL_REDIRECT: 'FUNCTIONAL_REDIRECT',
  // the NOTIFICATION_REDIRECT path is used to redirect to the appropriate page when a notification is clicked
  // from an email. it's a separate path from FUNCTIONAL_REDIRECT because there are enough different paths and parameters
  // being passed that it's simpler to have a separate path for it.
  NOTIFICATION_REDIRECT: 'NOTIFICATION_REDIRECT',

  // Top-level routes
  TASKS: 'TASKS',
  COLLABORATORS: 'COLLABORATORS',
  NOTIFICATIONS: 'NOTIFICATIONS',
  KNOWLEDGE_CENTER: 'KNOWLEDGE_CENTER',
  TASK_STANDALONE: 'TASK_STANDALONE',
  ADMIN: 'ADMIN',

  // Admin routes
  ADMIN_USERS: 'ADMIN_USERS',
  ADMIN_FORMS: 'ADMIN_FORMS',
  ADMIN_FORM_EDITOR: 'ADMIN_FORM_EDITOR',
  ADMIN_FORM_TESTS: 'ADMIN_FORM_TESTS',
  ADMIN_FORM_TEST_RUN: 'ADMIN_FORM_TEST_RUN',
  ADMIN_FORM_TEST_SUBMISSION: 'ADMIN_FORM_TEST_SUBMISSION',
  ADMIN_TEAMS: 'ADMIN_TEAMS',
  ADMIN_EDUCATION: 'ADMIN_EDUCATION',
  ADMIN_EDUCATION_SLIDE: 'ADMIN_EDUCATION_SLIDE',
  ADMIN_BRANDING: 'ADMIN_BRANDING',
  ADMIN_SUB_BRANDS: 'ADMIN_SUB_BRANDS',
  ADMIN_CATEGORIES: 'ADMIN_CATEGORIES',
  ADMIN_CONFIGURATIONS: 'ADMIN_CONFIGURATIONS',
  ADMIN_FEATURE_FLAGS: 'ADMIN_FEATURE_FLAGS',
  ADMIN_INTEGRATIONS: 'ADMIN_INTEGRATIONS',
  ADMIN_SFTP_FILES: 'ADMIN_SFTP_FILES',
  ADMIN_ORGANIZATIONS: 'ADMIN_ORGANIZATIONS',
  ADMIN_TENANTS: 'ADMIN_TENANTS',
  ADMIN_LEGAL_DISCLAIMERS: 'ADMIN_LEGAL_DISCLAIMERS',
  ADMIN_AUDIT_LOG: 'ADMIN_AUDIT_LOG',
  ADMIN_ROLES: 'ADMIN_ROLES',
  ADMIN_SECURITY: 'ADMIN_SECURITY',
  ADMIN_SUMMARY_PROMPTS: 'ADMIN_SUMMARY_PROMPTS',
  ADMIN_PROMPT_ITERATION: 'ADMIN_PROMPT_ITERATION',
  ADMIN_PRESENTATION_TEMPLATES: 'ADMIN_PRESENTATION_TEMPLATES',
  ADMIN_PRESENTATION_TEMPLATE_EDITOR: 'ADMIN_PRESENTATION_TEMPLATE_EDITOR',
  ADMIN_DOCUMENT_SUMMARIES_REPORT: 'ADMIN_DOCUMENT_SUMMARIES_REPORT',
  USER_SETTINGS: 'USER_SETTINGS',
  HOUSEHOLDS_LIST: 'HOUSEHOLDS_LIST',

  // Dev tools routes
  INTERNAL_DEV_TOOLS: 'INTERNAL_DEV_TOOLS',

  // Proposal routes
  HOUSEHOLD_PROPOSAL_CREATE: 'HOUSEHOLD_PROPOSAL_CREATE',
  HOUSEHOLD_PROPOSAL_EDIT: 'HOUSEHOLD_PROPOSAL_EDIT',
  HOUSEHOLD_PROPOSAL_PRESENTATION: 'HOUSEHOLD_PROPOSAL_PRESENTATION',

  // GRAT design and implementation routes
  HOUSEHOLD_ENTITY_IMPLEMENTATION: 'HOUSEHOLD_ENTITY_IMPLEMENTATION',
  HOUSEHOLD_ENTITY_DESIGNER: 'HOUSEHOLD_ENTITY_DESIGNER',

  // Gift designer routes
  HOUSEHOLD_GIFT_DESIGNER: 'HOUSEHOLD_GIFT_DESIGNER',
  HOUSEHOLD_GIFT_DESIGNER_BASIC_INFORMATION:
    'HOUSEHOLD_GIFT_DESIGNER_BASIC_INFORMATION',
  HOUSEHOLD_GIFT_DESIGNER_MODEL_SCENARIOS:
    'HOUSEHOLD_GIFT_DESIGNER_MODEL_SCENARIOS',

  // CRT/CLT designer routes
  HOUSEHOLD_GIFT_CHARITABLE_TRUST_DESIGNER:
    'HOUSEHOLD_GIFT_CHARITABLE_TRUST_DESIGNER',
  HOUSEHOLD_GIFT_CHARITABLE_TRUST_DESIGNER_BASIC_INFORMATION:
    'HOUSEHOLD_GIFT_CHARITABLE_TRUST_DESIGNER_BASIC_INFORMATION',
  HOUSEHOLD_GIFT_CHARITABLE_TRUST_DESIGNER_MODEL_SCENARIOS:
    'HOUSEHOLD_GIFT_CHARITABLE_TRUST_DESIGNER_MODEL_SCENARIOS',

  // Single-entity routes
  HOUSEHOLD_ENTITY_BASE: 'HOUSEHOLD_ENTITY_BASE',
  HOUSEHOLD_ENTITY_DETAILS: 'HOUSEHOLD_ENTITY_DETAILS',
  HOUSEHOLD_ENTITY_DOCUMENTS: 'HOUSEHOLD_ENTITY_DOCUMENTS',
  HOUSEHOLD_ENTITY_DOCUMENT_DETAILS: 'HOUSEHOLD_ENTITY_DOCUMENT_DETAILS',
  HOUSEHOLD_ENTITY_TASKS: 'HOUSEHOLD_ENTITY_TASKS',
  HOUSEHOLD_ENTITY_PERFORMANCE: 'HOUSEHOLD_ENTITY_PERFORMANCE',
  HOUSEHOLD_ENTITY_PAYMENTS: 'HOUSEHOLD_ENTITY_PAYMENTS',
  HOUSEHOLD_ENTITY_VALUATION_HISTORY: 'HOUSEHOLD_ENTITY_VALUATION_HISTORY',
  HOUSEHOLD_ENTITY_DETAILS_DIAGRAM: 'HOUSEHOLD_ENTITY_DETAILS_DIAGRAM',

  // Manage household routes
  MANAGE_HOUSEHOLD_NEW: 'MANAGE_HOUSEHOLD_NEW',
  MANAGE_HOUSEHOLD_DETAILS: 'MANAGE_HOUSEHOLD_DETAILS',
  MANAGE_HOUSEHOLD_ADMIN: 'MANAGE_HOUSEHOLD_ADMIN',
  HOUSEHOLD_KG_ADMIN: 'HOUSEHOLD_KG_ADMIN',

  // Single household routes
  SPECIFIC_HOUSEHOLD_REDIRECT: 'SPECIFIC_CLIENT_REDIRECT',
  UNSPECIFIED_HOUSEHOLD_REDIRECT: 'UNSPECIFIED_CLIENT_REDIRECT',
  HOUSEHOLD_DETAILS_OVERVIEW: 'HOUSEHOLD_DETAILS_OVERVIEW',
  HOUSEHOLD_DETAILS_TASKS: 'HOUSEHOLD_DETAILS_TASKS',
  HOUSEHOLD_DETAILS_PEOPLE_ORGANIZATIONS:
    'HOUSEHOLD_DETAILS_PEOPLE_ORGANIZATIONS',
  HOUSEHOLD_ACTIVITY_HISTORY: 'HOUSEHOLD_ACTIVITY_HISTORY',
  HOUSEHOLD_DETAILS_PROPOSALS: 'HOUSEHOLD_DETAILS_PROPOSALS',
  HOUSEHOLD_DETAILS_ENTITIES_MANAGE_INCOME_EXPENSES:
    'HOUSEHOLD_DETAILS_ENTITIES_MANAGE_INCOME_EXPENSES',
  HOUSEHOLD_DETAILS_PERFORMANCE: 'HOUSEHOLD_DETAILS_PERFORMANCE',
  HOUSEHOLD_DETAILS_REPORTING: 'HOUSEHOLD_DETAILS_REPORTING',
  HOUSEHOLD_DETAILS_PRESENTATIONS: 'HOUSEHOLD_DETAILS_PRESENTATIONS',
  HOUSEHOLD_DETAILS_PRESENTATION_V2: 'HOUSEHOLD_DETAILS_PRESENTATION_V2',
  /** Path to user-specific presentation template list page */
  HOUSEHOLD_DETAILS_PRESENTATION_TEMPLATES_USER_LIST:
    'HOUSEHOLD_DETAILS_PRESENTATION_TEMPLATES_USER_LIST',
  /** Path to user-specific presentation template editor */
  HOUSEHOLD_DETAILS_PRESENTATION_TEMPLATES_USER_EDITOR:
    'HOUSEHOLD_DETAILS_PRESENTATION_TEMPLATES_USER_EDITOR',
  HOUSEHOLD_DETAILS_GIFTING: 'HOUSEHOLD_DETAILS_GIFTING',
  HOUSEHOLD_DETAILS_GIFTING_ESTATE_TAX_CALCULATION_TROWSER:
    'HOUSEHOLD_DETAILS_GIFTING_ESTATE_TAX_CALCULATION_TROWSER',
  HOUSEHOLD_DETAILS_DOCUMENTS: 'HOUSEHOLD_DETAILS_DOCUMENTS',
  HOUSEHOLD_DETAILS_DOCUMENTS_MEMOS: 'HOUSEHOLD_DETAILS_DOCUMENTS_MEMOS',
  HOUSEHOLD_ARTIFACT_BUILDER: 'HOUSEHOLD_ARTIFACT_BUILDER',
  HOUSEHOLD_ARTIFACT_BUILDER_MEMO_DETAILS:
    'HOUSEHOLD_ARTIFACT_BUILDER_MEMO_DETAILS',
  HOUSEHOLD_DETAILS_DOCUMENT_DETAILS: 'HOUSEHOLD_DETAILS_DOCUMENT_DETAILS',
  HOUSEHOLD_DETAILS_MEMO_DETAILS: 'HOUSEHOLD_DETAILS_MEMO_DETAILS',
  HOUSEHOLD_FORM_SUBMISSIONS: 'HOUSEHOLD_FORM_SUBMISSIONS',
  HOUSEHOLD_FORM_SUBMISSION_RESULT: 'HOUSEHOLD_FORM_SUBMISSION_RESULT',

  // Knowledge graph page for a document
  DOCUMENT_KNOWLEDGE_GRAPH: 'DOCUMENT_KNOWLEDGE_GRAPH',
  DOCUMENT_KNOWLEDGE_GRAPH_V2: 'DOCUMENT_KNOWLEDGE_GRAPH_V2',

  // Single household waterfall routes
  HOUSEHOLD_DETAILS_ESTATE_WATERFALL_TAB:
    'HOUSEHOLD_DETAILS_ESTATE_WATERFALL_TAB',
  HOUSEHOLD_DETAILS_ESTATE_WATERFALL_TAB_DIAGRAM:
    'HOUSEHOLD_DETAILS_ESTATE_WATERFALL_TAB_DIAGRAM',
  HOUSEHOLD_DETAILS_ESTATE_WATERFALL_TAB_DIAGRAM_ESTATE_TAX_CALCULATION_TROWSER:
    'HOUSEHOLD_DETAILS_ESTATE_WATERFALL_TAB_DIAGRAM_ESTATE_TAX_CALCULATION_TROWSER',
  HOUSEHOLD_DETAILS_ESTATE_WATERFALL_TAB_SUMMARY:
    'HOUSEHOLD_DETAILS_ESTATE_WATERFALL_TAB_SUMMARY',
  HOUSEHOLD_DETAILS_ESTATE_WATERFALL_TAB_SUMMARY_ESTATE_TAX_CALCULATION_TROWSER:
    'HOUSEHOLD_DETAILS_ESTATE_WATERFALL_TAB_SUMMARY_ESTATE_TAX_CALCULATION_TROWSER',
  HOUSEHOLD_DETAILS_ESTATE_WATERFALL_TAB_LIST_ESTATE_TAX_CALCULATION_TROWSER:
    'HOUSEHOLD_DETAILS_ESTATE_WATERFALL_TAB_LIST_ESTATE_TAX_CALCULATION_TROWSER',
  HOUSEHOLD_DETAILS_ESTATE_WATERFALL_TAB_TAX_OVERVIEW_ESTATE_TAX_CALCULATION_TROWSER:
    'HOUSEHOLD_DETAILS_ESTATE_WATERFALL_TAB_TAX_OVERVIEW_ESTATE_TAX_CALCULATION_TROWSER',
  HOUSEHOLD_DETAILS_ESTATE_WATERFALL_TAB_LIST:
    'HOUSEHOLD_DETAILS_ESTATE_WATERFALL_TAB_LIST',
  HOUSEHOLD_DETAILS_ESTATE_WATERFALL_TAB_TAX_OVERVIEW:
    'HOUSEHOLD_DETAILS_ESTATE_WATERFALL_TAB_TAX_OVERVIEW',
  HOUSEHOLD_DETAILS_ESTATE_WATERFALL_BENEFICIARY_REPORTING:
    'HOUSEHOLD_DETAILS_ESTATE_WATERFALL_BENEFICIARY_REPORTING',
  HOUSEHOLD_DETAILS_ESTATE_WATERFALL_BENEFICIARY_REPORTING_OVERVIEW:
    'HOUSEHOLD_DETAILS_ESTATE_WATERFALL_BENEFICIARY_REPORTING_OVERVIEW',
  HOUSEHOLD_DETAILS_ESTATE_WATERFALL_BENEFICIARY_REPORTING_BENEFICIARY_DETAILS:
    'HOUSEHOLD_DETAILS_ESTATE_WATERFALL_BENEFICIARY_REPORTING_BENEFICIARY_DETAILS',

  // Bulk import routes
  IMPORT_INTEGRATION_CLIENTS: 'IMPORT_INTEGRATION_CLIENTS',
  IMPORT_INTEGRATION_ENTITIES_BULK: 'IMPORT_INTEGRATION_ENTITIES_BULK',
  IMPORT_CSV_VALUATIONS: 'IMPORT_CSV_VALUATIONS',

  // Print-only routes
  __PRINT_ONLY_SINGLE_ENTITY_WITH_NOTES:
    '__PRINT_ONLY_SINGLE_ENTITY_WITH_NOTES',
  __PRINT_ONLY_CLIENT_PRESENTATION: '__PRINT_ONLY_CLIENT_PRESENTATION',
  __PRINT_ONLY_CLIENT_PRESENTATION_V2: '__PRINT_ONLY_CLIENT_PRESENTATION_V2',
  __PRINT_ONLY_CLIENT_PROPOSAL: '__PRINT_ONLY_CLIENT_PROPOSAL',
  __PRINT_ONLY_CLIENT_REPORTING: '__PRINT_ONLY_CLIENT_REPORTING',
  __PRINT_ONLY_WATERFALL_COMPARISON: '__PRINT_ONLY_WATERFALL_COMPARISON',
  __PRINT_ONLY_WATERFALL_COMPARISON_LEGACY:
    '__PRINT_ONLY_WATERFALL_COMPARISON_LEGACY',
  __PRINT_ONLY_DOCUMENT_SUMMARY_EXPORT_V2:
    '__PRINT_ONLY_DOCUMENT_SUMMARY_EXPORT_V2',
  __PRINT_ONLY_MEMO_EXPORT: '__PRINT_ONLY_MEMO_EXPORT',
  __PRINT_ONLY_FORM_REVIEW: '__PRINT_ONLY_FORM_REVIEW',
  __PRINT_ONLY_ESTATE_INSIGHTS: '__PRINT_ONLY_ESTATE_INSIGHTS',
  __PRINT_ONLY_EDUCATION_SLIDE: '__PRINT_ONLY_EDUCATION_SLIDE',
  __PRINT_ONLY_ESTATE_SNAPSHOT: '__PRINT_ONLY_ESTATE_SNAPSHOT',

  // Client team
  HOUSEHOLD_DETAILS_PEOPLE_CLIENT_TEAM: 'HOUSEHOLD_DETAILS_PEOPLE_CLIENT_TEAM',
  HOUSEHOLD_DETAILS_FAMILY_TREE: 'HOUSEHOLD_DETAILS_FAMILY_TREE',

  // Sidebar container routes
  HOUSEHOLD_SIDEBAR_CASHFLOWS: 'HOUSEHOLD_SIDEBAR_CASHFLOWS',
  HOUSEHOLD_SIDEBAR_GIFTING: 'HOUSEHOLD_SIDEBAR_GIFTING',
  HOUSEHOLD_SIDEBAR_ESTATE: 'HOUSEHOLD_SIDEBAR_ESTATE',
  HOUSEHOLD_SIDEBAR_PEOPLE: 'HOUSEHOLD_SIDEBAR_PEOPLE',
  HOUSEHOLD_SIDEBAR_ACCESS: 'HOUSEHOLD_SIDEBAR_ACCESS',
  HOUSEHOLD_SIDEBAR_OWNERSHIP: 'HOUSEHOLD_SIDEBAR_OWNERSHIP',

  // Insights route
  HOUSEHOLD_INSIGHTS: 'HOUSEHOLD_INSIGHTS',

  // Balance sheet data organization routes
  HOUSEHOLD_ESTATE_BALANCE_SHEET: 'HOUSEHOLD_ESTATE_BALANCE_SHEET',
  HOUSEHOLD_ESTATE_DIRECT_HOLDINGS: 'HOUSEHOLD_ESTATE_DIRECT_HOLDINGS',
  HOUSEHOLD_ESTATE_BUSINESSES: 'HOUSEHOLD_ESTATE_BUSINESSES',
  HOUSEHOLD_ESTATE_LIFE_INSURANCE: 'HOUSEHOLD_ESTATE_LIFE_INSURANCE',
  HOUSEHOLD_ESTATE_TRUSTS: 'HOUSEHOLD_ESTATE_TRUSTS',
  HOUSEHOLD_ESTATE_ENTITIES_MANAGE_LIABILITIES:
    'HOUSEHOLD_ESTATE_ENTITIES_MANAGE_LIABILITIES',
  HOUSEHOLD_ESTATE_ENTITIES: 'HOUSEHOLD_ESTATE_ENTITIES',
  HOUSEHOLD_ESTATE_MANAGE_DISPOSITIONS: 'HOUSEHOLD_ESTATE_MANAGE_DISPOSITIONS',
  HOUSEHOLD_ESTATE_MANAGE_DISPOSITIONS_ESTATE_TAX_CALCULATION_TROWSER:
    'HOUSEHOLD_ESTATE_MANAGE_DISPOSITIONS_ESTATE_TAX_CALCULATION_TROWSER',
  HOUSEHOLD_OWNERSHIP_ENTITY_MAP: 'HOUSEHOLD_OWNERSHIP_ENTITY_MAP',
  HOUSEHOLD_OWNERSHIP_ENTITY_MAP_DIAGRAM:
    'HOUSEHOLD_OWNERSHIP_ENTITY_MAP_DIAGRAM',
  ESTATE_WATERFALL_MANAGE_DISPOSITIONS: 'ESTATE_WATERFALL_MANAGE_DISPOSITIONS',
  ESTATE_WATERFALL_MANAGE_DISPOSITIONS_ESTATE_TAX_CALCULATION_TROWSER:
    'ESTATE_WATERFALL_MANAGE_DISPOSITIONS_ESTATE_TAX_CALCULATION_TROWSER',

  // Education
  EDUCATION: 'EDUCATION',
} as const;

export type RouteKey = keyof typeof ROUTE_KEYS;

export enum GratDesignerStages {
  BASIC_INFORMATION = 'basic-information',
  STRUCTURING = 'structuring',
}

export enum GiftDesignerStages {
  BASIC_INFORMATION = 'basic-information',
  MODEL_SCENARIOS = 'model-scenarios',
}

export enum CharitableTrustDesignerStages {
  BASIC_INFORMATION = 'basic-information',
  MODEL_SCENARIOS = 'model-scenarios',
}

export type AugmentedHandle = Match['handle'] & {
  features?: {
    /** Set this flag to `true` if a route and all its children should not display the global sidebar. */
    hideGlobalSidebar?: boolean;
  };
};

export type AugmentedRoute = Omit<RouteObject, 'children'> & {
  key: string;
  props?: Record<string, unknown>;
  children?: AugmentedRoute[];
  handle?: AugmentedHandle;
};
